import { useCallback, useEffect, useState } from 'react';
import { Route, Loader2, PlayCircle, Save, Flame, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { leadRoutingAPI } from '@/lib/api';
import { LeadTriageBadge } from './LeadTriageBadge';

const CounsellorPool = ({ staff, selected, toggle }) => (
  <div className="rounded-xl border border-gray-200 dark:border-slate-600 divide-y divide-gray-100 dark:divide-slate-700 max-h-80 overflow-y-auto" data-testid="routing-counsellor-list">
    {[...staff].sort((a, b) => Number(selected.includes(b.id)) - Number(selected.includes(a.id))).map((s) => (
      <label key={s.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700/50" data-testid={`routing-counsellor-${s.id}`}>
        <Checkbox checked={selected.includes(s.id)} onCheckedChange={() => toggle(s.id)} data-testid={`routing-counsellor-checkbox-${s.id}`} />
        <div className="min-w-0 flex-1">
          <p className="text-sm text-gray-800 dark:text-white truncate">{s.full_name}</p>
          <p className="text-xs text-gray-500 truncate">{s.email}</p>
        </div>
        <span className="text-xs text-gray-500 shrink-0">{s.open_leads} open</span>
      </label>
    ))}
  </div>
);

const RoutingLog = ({ log }) => (
  <div data-testid="routing-log">
    <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">Recent routing decisions</h4>
    {log.length === 0 && <p className="text-xs text-gray-500">No leads routed yet.</p>}
    <div className="space-y-2 max-h-[460px] overflow-y-auto pr-1">
      {log.map((r) => (
        <div key={r.id} className="rounded-xl border border-gray-200 dark:border-slate-600 px-3 py-2" data-testid={`routing-log-${r.lead_id}`}>
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-medium text-gray-800 dark:text-white truncate">{r.lead_name || 'Unnamed lead'}</p>
            <LeadTriageBadge triage={r} />
          </div>
          <p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5">
            <Users className="w-3 h-3 inline mr-1" />{r.routed_to_name} · {new Date(r.at).toLocaleString()}
          </p>
          {r.reason && <p className="text-xs text-gray-500 mt-1">{r.reason}</p>}
          {r.routing_reason && <p className="text-xs text-teal-700 dark:text-teal-300 mt-0.5">{r.routing_reason}</p>}
        </div>
      ))}
    </div>
  </div>
);

export const LeadRoutingPanel = () => {
  const [data, setData] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [selected, setSelected] = useState([]);
  const [log, setLog] = useState([]);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    try {
      const [{ data: s }, { data: l }] = await Promise.all([leadRoutingAPI.getSettings(), leadRoutingAPI.getLog()]);
      setData(s); setEnabled(s.enabled); setSelected(s.counsellor_ids || []); setLog(l);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not load routing settings'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = (id) => setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const save = async () => {
    setSaving(true);
    try {
      await leadRoutingAPI.saveSettings({ enabled, counsellor_ids: selected });
      toast.success('Routing settings saved');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const run = async () => {
    setRunning(true);
    try {
      const { data: r } = await leadRoutingAPI.run(50);
      toast.success(`Triaging ${r.queued} unassigned leads — results appear below as they finish`);
      setTimeout(load, 15000);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not start triage'); }
    setRunning(false);
  };

  if (!data) return <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-teal-600" /></div>;

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="lead-routing-panel">
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-4 rounded-xl border border-gray-200 dark:border-slate-600 p-4">
          <div>
            <p className="text-sm font-semibold text-gray-800 dark:text-white flex items-center gap-1.5"><Route className="w-4 h-4 text-teal-600" /> Auto-route new leads</p>
            <p className="text-xs text-gray-500 mt-1">Claude scores every new unassigned lead (hot / warm / cold) and assigns it to the best-fit counsellor, then the lightest workload. The counsellor gets a notification.</p>
          </div>
          <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="routing-enabled-switch" />
        </div>
        <div>
          <p className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Counsellors who receive leads ({selected.length})</p>
          <CounsellorPool staff={data.staff} selected={selected} toggle={toggle} />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={save} disabled={saving} data-testid="routing-save-btn" className="bg-teal-600 hover:bg-teal-700 text-white">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save settings
          </Button>
          <Button variant="outline" onClick={run} disabled={running || !data.enabled || data.running} data-testid="routing-run-btn">
            <PlayCircle className="w-4 h-4 mr-1" /> Triage unassigned now
          </Button>
        </div>
        <p className="text-xs text-gray-500 flex items-center gap-1" data-testid="routing-unassigned-count">
          <Flame className="w-3.5 h-3.5 text-orange-500" /> {data.unassigned_new_leads} new leads are unassigned{data.running ? ' · triage in progress…' : ''}. "Triage now" handles up to 50 at a time.
        </p>
      </div>
      <RoutingLog log={log} />
    </div>
  );
};
